import {
  DepressionContentPage,
  FAQItem,
} from "@/components/DepressionContentPage";
import { RelatedLink } from "@/components/RelatedPages";
import { SaintComparisonStatBand } from "@/components/SaintComparisonInline";

const faqs: FAQItem[] = [
  {
    question:
      "Can antidepressants cause dry mouth, headaches, and stomach problems?",
    answer:
      "Yes. Dry mouth is one of the most frequent complaints across antidepressants, stimulants, and benzodiazepines. Headaches are common with SSRIs, especially at higher doses. Gastrointestinal problems — nausea, diarrhea, constipation, cramping — are extremely common with SSRIs because most of the body's serotonin receptors are in the gut, not the brain. Many people who've been on an SSRI for years assume they simply have a sensitive stomach.",
  },
  {
    question: "What is emotional blunting on antidepressants?",
    answer:
      "Emotional blunting is the sense that both the highs and lows have been sanded down — less despair, but also less joy. For some people this is acceptable. For others, it's the thing that makes them wonder whether the medication is treating the depression or just muffling it. It is a recognized side effect of SSRIs and related medications.",
  },
  {
    question:
      "What is the remission rate for another antidepressant after prior ones have failed?",
    answer:
      "For someone who has already failed prior antidepressants, the chance that the next one produces full remission is around 14 percent, and it takes six to twelve weeks to find out. That's two to three months of new side effects for a roughly one-in-seven shot.",
  },
  {
    question: "Does TMS have the same side effects as medication?",
    answer:
      "No. TMS uses focused magnetic pulses delivered through a coil against the scalp to stimulate the mood-regulating region of the brain directly. Nothing is swallowed, injected, or absorbed, so there is no effect on your gut, mouth, sleep architecture, or sex drive. The side effects that do exist — mild headache, fatigue, and temporary scalp discomfort — are local and short-lived.",
  },
  {
    question: "Do I have to stop my medication to do SAINT?",
    answer:
      "No. Most patients continue their existing medication regimen through treatment. SAINT is not a replacement for what you've built — it is a different kind of intervention aimed at the brain circuitry your medications may not have reached. Never change or stop a psychiatric medication without talking to your prescribing provider.",
  },
  {
    question: "What should I bring to my next appointment?",
    answer:
      "Write down every medication you're taking, with doses, including the 'as needed' ones; every medication you've stopped and why; the physical symptoms you've stopped mentioning; and what each medication has actually done for your mood, honestly. That information is the foundation of any good treatment decision.",
  },
];

const related: RelatedLink[] = [
  {
    title: "You've Tried Everything. Why Is the Depression Still Here?",
    href: "/youve-tried-everything-why-is-the-depression-still-here",
    description:
      "What treatment-resistant depression means and why medications don't work for everyone.",
  },
  {
    title: "How SAINT Compares to Other Depression Treatment Options",
    href: "/how-saint-compares-to-other-depression-treatment-options",
    description:
      "A side-by-side comparison of SAINT, standard TMS, ECT, esketamine, and medication.",
  },
  {
    title: "Am I a Candidate for TMS?",
    href: "/am-i-a-candidate",
    description: "What clinicians consider when evaluating TMS eligibility.",
  },
  {
    title: "SAINT® TMS in Coral Springs, FL",
    href: "/saint",
    description: "The full SAINT treatment page with outcomes, cost, and FAQs.",
  },
  {
    title: "Why Am I So Tired All the Time? Depression Fatigue",
    href: "/learn/always-tired-depression-fatigue",
    description: "How depression drains energy and why rest doesn't fix it.",
  },
  {
    title: "High-Functioning Depression Symptoms: 10 Quiet Signs",
    href: "/high-functioning-depression-symptoms-treatment",
    description: "Everyday habits that can be quiet signs of depression.",
  },
];

export default function SideEffectsStoppedMentioning() {
  return (
    <DepressionContentPage
      seo={{
        title:
          "Medication Side Effects You've Stopped Mentioning | Neuro Wellness",
        description:
          "Dry mouth, headaches, stomach trouble, emotional blunting — when medication side effects become background noise and why some people look beyond medication to TMS and SAINT.",
        canonical: "/side-effects-youve-stopped-mentioning",
        keywords:
          "antidepressant side effects, dry mouth medication, SSRI stomach problems, emotional blunting, medication side effects depression, TMS no systemic side effects, SAINT TMS, treatment-resistant depression, medication burden, psychiatric medication side effects",
      }}
      badge="Clinical Insights"
      heroTitle="The Side Effects You've Stopped Mentioning: Dry Mouth, Headaches, Stomach Trouble, and Why Some People Look Beyond Medication"
      heroSubtitle="There's a category of symptoms that people on psychiatric medications stop reporting after a while. Not because the symptoms went away, but because they've become part of the background. This article is an argument for taking the trade-offs seriously — especially if the medications aren't fully working."
      lastUpdated="2026-11-03"
      sections={[
        {
          heading: "The common ones",
          body: (
            <div className="space-y-4">
              <p>
                There's a category of symptoms that people on psychiatric
                medications stop reporting after a while. Not because the
                symptoms went away, but because they've become part of the
                background. The dry mouth that means you carry a water bottle
                everywhere. The headaches that show up most afternoons. The
                stomach that's never quite settled, that you've started calling
                IBS because that's easier than explaining it.
              </p>
              <p>
                You may not even connect these to your medication anymore.
                They've been there so long they feel like part of you. But when
                you list your medications at a new appointment — an
                antidepressant at a high dose, a stimulant, a sleep aid,
                something as-needed for anxiety — a clinician sees a different
                picture: a body that's processing four different psychiatric
                drugs, each with its own side-effect profile, all at once.
              </p>
              <p>
                This article isn't an argument against medication. For many
                people, it works and it's worth the trade-offs. It's an argument
                for taking the trade-offs seriously, especially if the
                medications aren't fully working.
              </p>

              {/* Visual 1: Common side effects people stop mentioning */}
              <div className="my-6 overflow-hidden rounded-2xl border border-border bg-card shadow-soft not-prose">
                <div className="relative aspect-[4/3] sm:aspect-[16/10] w-full overflow-hidden bg-muted">
                  <img
                    src="https://vibe.filesafe.space/1787327131284421342/attachments/c24901b1-34e5-479c-979c-1fe3a804504b.png"
                    alt="Infographic: Common side effects people stop mentioning including emotional numbness, constant fatigue, brain fog, weight or appetite changes, sleep disruption, and lower sex drive"
                    className="h-full w-full object-contain bg-white"
                    loading="lazy"
                  />
                </div>
                <div className="p-3.5 bg-card border-t border-border">
                  <p className="font-headings text-sm font-bold text-primary">
                    Side Effects That Gradually Become Background Noise
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                    You do not have to ignore changes that affect your daily
                    life. Emotional blunting, exhaustion, brain fog, appetite
                    shifts, and sleep disruption are legitimate treatment
                    concerns.
                  </p>
                </div>
              </div>

              <ul className="list-disc pl-5 space-y-2">
                <li>
                  <strong>Dry mouth.</strong> One of the most frequent
                  complaints across antidepressants, stimulants, and
                  benzodiazepines alike. It's annoying on its own, and over time
                  it increases the risk of dental problems.
                </li>
                <li>
                  <strong>Headaches.</strong> Common with SSRIs, especially at
                  higher doses, and with stimulants. Frequent headaches can also
                  be a symptom of the depression and anxiety themselves, which
                  makes them hard to untangle.
                </li>
                <li>
                  <strong>Gastrointestinal problems.</strong> SSRIs act on
                  serotonin, and most of the body's serotonin receptors are in
                  the gut, not the brain. Nausea, diarrhea, constipation, and
                  cramping are extremely common, and many people who've been on
                  an SSRI for years assume they simply have a sensitive stomach.
                </li>
                <li>
                  <strong>Sleep disruption.</strong> Stimulants can make it hard
                  to fall asleep. SSRIs can fragment sleep. Then a sleep aid
                  gets added, which can cause next-day grogginess. The
                  medications end up managing each other's effects.
                </li>
                <li>
                  <strong>Emotional blunting.</strong> Less talked about, but
                  common: the sense that the highs and lows have both been
                  sanded down. Less despair, but also less joy. For some people
                  this is acceptable. For others, it's the thing that makes them
                  wonder whether the medication is treating the depression or
                  just muffling it.
                </li>
                <li>
                  <strong>
                    Sexual side effects, weight changes, sweating, jitteriness.
                  </strong>{" "}
                  Each medication has its own list.
                </li>
              </ul>
            </div>
          ),
        },
        {
          heading: "When the math stops working",
          body: (
            <div className="space-y-4">
              <p>
                Every medication decision is a balance: benefit against burden.
                When a drug clearly works, side effects are easier to accept.
                The problem is when the benefit is partial and the burden is
                real.
              </p>
              <p>
                If you're on multiple medications, still meeting criteria for
                major depression, and also managing a running list of physical
                complaints, it's worth stepping back and asking what the regimen
                is actually accomplishing. Not to abandon it, but to ask whether
                adding a fifth medication is likely to change the answer.
              </p>

              {/* Visual 2: What side effects can look like in daily life */}
              <div className="my-6 overflow-hidden rounded-2xl border border-border bg-card shadow-soft not-prose">
                <div className="relative aspect-[4/3] sm:aspect-[16/10] w-full overflow-hidden bg-muted">
                  <img
                    src="https://vibe.filesafe.space/1787327131284421342/attachments/8cb1d54b-6b20-47b4-883f-a6d242c3058a.png"
                    alt="Infographic: What side effects can look like in daily life — still going through the motions, trouble concentrating, less energy for the day, feeling emotionally flat, sleep feels off, avoiding intimacy"
                    className="h-full w-full object-contain bg-white"
                    loading="lazy"
                  />
                </div>
                <div className="p-3.5 bg-card border-t border-border">
                  <p className="font-headings text-sm font-bold text-primary">
                    How Side Effects Show Up Day to Day
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                    Subtle changes still matter. Going through the motions,
                    difficulty concentrating, lowered energy, flat affect, and
                    disrupted sleep are often chalked up to &ldquo;just how
                    things are.&rdquo;
                  </p>
                </div>
              </div>

              <p>
                The numbers on that question are sobering. For someone who has
                already failed prior antidepressants, the chance that the next
                one produces full remission is around 14 percent, and it takes
                six to twelve weeks to find out. That's two to three months of
                new side effects for a roughly one-in-seven shot.
              </p>
            </div>
          ),
        },
        {
          heading: "Why medication has a ceiling for some people",
          body: (
            <div className="space-y-4">
              <p>
                Antidepressants work on brain chemistry, and for many people
                that's enough. But depression is also a circuitry problem. Brain
                imaging consistently shows that the region behind the forehead
                called the dorsolateral prefrontal cortex, which regulates mood,
                motivation, and attention, is underactive in depression.
                Medication changes the chemical environment around that circuit.
                It doesn't directly retrain it.
              </p>
              <p>
                That's the reason someone can be on a high dose of a good
                medication, take it faithfully, tolerate the dry mouth and the
                headaches and the stomach trouble, and still feel fundamentally
                unchanged. The side effects are systemic because the drug is
                systemic; it goes everywhere in the body to reach one region of
                the brain. And even when it gets there, it's working on the
                chemistry around the circuit, not the circuit itself.
              </p>
            </div>
          ),
        },
        {
          heading: "A treatment that doesn't enter your bloodstream",
          body: (
            <div className="space-y-4">
              <p>
                Transcranial magnetic stimulation (TMS) works differently. It
                uses focused magnetic pulses, delivered through a coil placed
                against the scalp, to stimulate the underactive mood-regulation
                region directly. Nothing is swallowed, injected, or absorbed.
                There's no effect on your gut, your mouth, your sleep
                architecture, or your sex drive. The side effects that do exist
                are local and short-lived.
              </p>
              <p>
                SAINT (Stanford Accelerated Intelligent Neuromodulation Therapy)
                is an accelerated, FDA-cleared form of TMS developed at
                Stanford's Brain Stimulation Lab. It's indicated for adults with
                major depressive disorder who haven't gotten adequate relief
                from antidepressants in their current episode. Here's how it
                works at{" "}
                <a
                  href="/find-a-verified-saint-provider"
                  className="font-semibold text-accent underline underline-offset-2 hover:text-accent/80"
                >
                  a Verified SAINT Provider
                </a>{" "}
                like Neuro Wellness TMS Centers of America:
              </p>

              {/* Visual 3: Ways to get support (Talk with provider, medication review, therapy, lifestyle, TMS, SAINT) */}
              <div className="my-6 overflow-hidden rounded-2xl border border-border bg-card shadow-soft not-prose">
                <div className="relative aspect-[4/3] sm:aspect-[16/10] w-full overflow-hidden bg-muted">
                  <img
                    src="https://vibe.filesafe.space/1787327131284421342/attachments/1b4c5aae-33a3-4801-af5d-bc4f4f820865.png"
                    alt="Overview of care avenues: Talk with your provider, medication review, talk therapy, lifestyle support, TMS, and SAINT accelerated TMS"
                    className="h-full w-full object-contain bg-white"
                    loading="lazy"
                  />
                </div>
                <div className="p-3.5 bg-card border-t border-border">
                  <p className="font-headings text-sm font-bold text-primary">
                    Ways to Get Support Beyond Another Medication Change
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                    The best plan is the one tailored to you. When medications
                    stall, exploring non-invasive neuromodulation like standard
                    or accelerated SAINT® TMS gives your care team a
                    circuit-level option.
                  </p>
                </div>
              </div>

              <ol className="list-decimal pl-5 space-y-3">
                <li>
                  <strong>Consultation (Zoom web conference).</strong> You meet
                  with our clinical team over a convenient Zoom call. As your
                  evaluation evolves, you will also have the option to meet with
                  our psychiatrist by Zoom web conference or in person at our
                  clinic. You meet with our clinical team to review every
                  medication you've taken, what each one did and didn't do, and
                  the physical symptoms you've been living with, and to confirm
                  you're a candidate.
                </li>
                <li>
                  <strong>fMRI brain scan (mandatory in person).</strong> A
                  roughly 45-minute functional MRI maps how your individual
                  brain networks are wired. (Mandatory in-person scan at an
                  imaging partner center.)
                </li>
                <li>
                  <strong>Personalized targeting.</strong> A targeting algorithm
                  reads your scan and identifies the exact location for
                  stimulation. Neuronavigation guides the coil to that spot for
                  every session.
                </li>
                <li>
                  <strong>
                    Five-day stimulation series (mandatory in person).
                  </strong>{" "}
                  In-person treatment Monday through Friday at our Coral Springs
                  clinic. Ten 10-minute sessions per day, with 50-minute breaks
                  in between, for five consecutive days. Five clinic visits,
                  compared with 30 to 36 for conventional TMS. Most patients
                  notice a change in mood within the first couple of days.
                </li>
              </ol>
            </div>
          ),
        },
        {
          heading: "What the results and side effects actually look like",
          body: (
            <div className="space-y-4">
              <SaintComparisonStatBand />
              <p>
                In the clinical trials behind SAINT's FDA clearance,
                participants had treatment-resistant depression and had already
                failed prior treatments. Seventy-nine percent reached remission,
                with an average time to remission of 2.6 days. Across more than
                400 trial patients, 85 percent experienced symptom relief, with
                no serious adverse events.
              </p>
              <p>
                Compare the side-effect list to what you're carrying now. The
                most commonly reported effects were mild headache, fatigue, and
                temporary scalp discomfort at the treatment site, typically
                resolving on their own. No dry mouth. No GI disruption. No
                weight change. No sexual side effects. SAINT isn't appropriate
                for people with non-removable metal in or near the head or an
                implanted device, and you'll be screened for these at your
                consultation.
              </p>
              <p>
                You don't have to stop your current medications to pursue SAINT.
                Most patients continue their existing regimen through treatment.
                But many find that once the depression lifts, the conversation
                with their prescriber about simplifying the regimen becomes
                possible for the first time.
              </p>
            </div>
          ),
        },
        {
          heading: "What to bring to your next appointment",
          body: (
            <div className="space-y-4">
              <p>
                Whether you're seeing your current prescriber or coming in for a
                consultation, write this down:
              </p>

              {/* Visual 4: When it may be time to speak up checklist */}
              <div className="my-6 overflow-hidden rounded-2xl border border-border bg-card shadow-soft not-prose">
                <div className="relative aspect-[4/3] sm:aspect-[16/10] w-full overflow-hidden bg-muted">
                  <img
                    src="https://vibe.filesafe.space/1787327131284421342/attachments/bf745bb1-6010-4574-9281-53e9f8a6bfb8.png"
                    alt="Checklist: When it may be time to speak up — You feel unlike yourself, side effects are affecting relationships, sleep is worse, feeling emotionally flat, fatigue is harder, you stopped bringing it up"
                    className="h-full w-full object-contain bg-white"
                    loading="lazy"
                  />
                </div>
                <div className="p-3.5 bg-card border-t border-border">
                  <p className="font-headings text-sm font-bold text-primary">
                    Signs Your Current Treatment Deserves a Closer Look
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                    A conversation with a qualified provider can clarify whether
                    your medication burden is still serving you or whether an
                    alternative path makes more sense.
                  </p>
                </div>
              </div>

              <ul className="list-disc pl-5 space-y-2">
                <li>
                  Every medication you're taking, with doses, including the "as
                  needed" ones
                </li>
                <li>Every medication you've stopped, and why</li>
                <li>The physical symptoms you've stopped mentioning</li>
                <li>
                  What each medication has actually done for your mood, honestly
                </li>
              </ul>
              <p>
                That information is the foundation of any good treatment
                decision.
              </p>
            </div>
          ),
        },
        {
          heading: "What to do next",
          body: (
            <div className="space-y-4">
              {/* Visual 5: Warm, reassuring clinician-patient consultation setting */}
              <div className="my-4 overflow-hidden rounded-2xl border border-border bg-card shadow-soft not-prose">
                <div className="relative aspect-[16/9] w-full overflow-hidden bg-muted">
                  <img
                    src="https://vibe.filesafe.space/1787327131284421342/attachments/c0b94f9b-6e9b-451f-9dd4-9d245c7b63e8.png"
                    alt="Compassionate doctor and patient reviewing treatment options together in a warm, private consultation room"
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-3.5 bg-card border-t border-border">
                  <p className="font-headings text-sm font-bold text-primary">
                    A Private, Unhurried Evaluation
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                    Review your entire medication history, discuss what you've
                    experienced, and get an honest clinical assessment of
                    whether TMS or SAINT® makes sense.
                  </p>
                </div>
              </div>

              <p>
                If you've been carrying side effects for years in exchange for
                partial relief, you're allowed to want a better deal.
              </p>
              <p>
                At Neuro Wellness TMS Centers of America, the first appointment
                is an evaluation, not a commitment. We'll do a full review of
                your medication history and its effects, both the intended ones
                and the ones you've learned to live with, and tell you plainly
                whether SAINT is a good option for you. We offer private
                outpatient care with a dedicated care team and weekend
                availability. Insurance coverage varies by plan; our team can
                help you understand your options, including prior authorization
                and self-pay.
              </p>
              <p className="font-semibold text-primary">
                Schedule a consultation:{" "}
                <a
                  href="tel:9548279793"
                  className="text-primary hover:underline"
                >
                  954-827-9793
                </a>
              </p>
              <p className="text-xs text-muted-foreground italic pt-2">
                Never stop or change a psychiatric medication without talking to
                your prescriber. If you are in crisis or thinking about harming
                yourself, please call or text 988 (Suicide and Crisis Lifeline)
                or go to your nearest emergency room. This article is for
                educational purposes and is not a substitute for individual
                medical advice.
              </p>
            </div>
          ),
        },
      ]}
      faq={faqs}
      relatedLinks={related}
      finalCtaText="Schedule a Consultation"
    />
  );
}
